'use client';
import React, { useMemo, useRef } from 'react';
import { exams } from '../../../../api/exams';
import { useMutation } from 'react-query';
import toast, { LoaderIcon } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import ProgressBar from './Progress';
import Rating from '@mui/material/Rating';
import Box from '@mui/material/Box';
import StarIcon from '@mui/icons-material/Star';
import JoditEditor from 'jodit-react';

type Question = {
  id: string;
  text: string;
  section: { title: string };
  rating_range_from: number;
  rating_range_to: number;
  type: string;
  tag?: string;
  answers: { id: string; text: string; rating: number }[];
};

type Props = {
  questions: Question[];
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
};

const Employee = ({ questions, currentIndex, setCurrentIndex }: Props) => {
  const question = questions[currentIndex];
  const navigate = useNavigate();
  const editor = useRef(null);
  const [hasLoaded, setHasLoaded ] = React.useState(false);
  const [hover, setHover] = React.useState(-1);
  const activeAssessment = JSON.parse(
    window.sessionStorage.getItem('active-assessment') || '{}'
  );

  const userId =
    JSON.parse(sessionStorage.getItem('user') || '{}')?.user?.user_id ||
    JSON.parse(sessionStorage.getItem('user') || '{}')?.user_id ||
    window.sessionStorage.getItem('user_id');

  const assessmentType = sessionStorage.getItem('assessment-type');

  const [answers, setAnswers] = React.useState<{
    [questionId: string]: {
      rating: number | null;
      explanation: string;
      duration: number;
      startTime: number;
      selectedAnswers: { answerId: string; text: string }[];
      selectedAnswerIds: string[]; // For satisfaction scale and select types
    };
  }>({});

  const currentAnswer = answers[question.id] || { 
    rating: null, 
    explanation: '', 
    selectedAnswerIds: [] 
  };

  const [error, setError] = React.useState<string>('');

  const finishSurveyMutation = useMutation(
    assessmentType === 'review'
      ? exams?.gradeSurveyReviewv2
      : exams.gradeAssessmentv2,
    {
      onSuccess: () => {
        toast.remove();
        toast.success(
          `${assessmentType === 'review' ? 'Review' : 'Survey'} submitted successfully`
        );
        navigate(`/candidate/${assessmentType === 'review' ? 'my-review' : 'self-review'}`);
      },
      onError: () => {
        toast.remove();
        toast.error(`Error Submitting ${assessmentType === 'review' ? 'Review' : 'Survey'}`);
      },
    }
  );

  const updateAnswersMutation = useMutation(
    assessmentType === 'review' ? exams.updateAnswersv2Review : exams.updateAnswersv2,
    {
      onSuccess: () => {
        finishSurveyMutation.mutate({
          userId: assessmentType === 'review' ? activeAssessment?.user_id : userId,
          reviewerId: assessmentType === 'review' ? userId : null,
          examId: window.sessionStorage.getItem('active-assessment-id'),
          userExamId: window.sessionStorage.getItem('active-assessment-user-id'),
          attempt: 1,
        });
      },
      onMutate: () => {
        toast.loading(`Submitting ${assessmentType === 'review' ? 'Review' : 'Survey'}`);
      },
      onError: () => {
        toast.remove();
        toast.error(`Error Submitting ${assessmentType === 'review' ? 'Review' : 'Survey'}`);
      },
    }
  );

  const draftAnswersMutation = useMutation(
    assessmentType === 'review' ? exams.updateAnswersv2Review : exams.updateAnswersv2,
    {
      onSuccess: () => {
        toast.remove();
        navigate(`/candidate/${assessmentType === 'review' ? 'my-review' : 'dashboard'}`);
      },
      onMutate: () => {
        toast.loading(`Saving ${assessmentType === 'review' ? 'Review' : 'Survey'}`);
      },
      onError: () => {
        toast.remove();
        toast.error(`Error Saving ${assessmentType === 'review' ? 'Review' : 'Survey'}`);
      },
    }
  );

  React.useEffect(() => {
  const existingAnswer = answers[question.id];
  // If answer doesn't exist yet, initialize it with saved data
  if (!existingAnswer) {
    const answeredCount = questions.filter(q =>
      q.answers?.some((ans: any) => ans?.status === true)
    )?.length;
    if(answeredCount !== 0 && !hasLoaded){
      setHasLoaded(true)
      setCurrentIndex(answeredCount - 1)
    }
    const firstAnswer: any = question?.answers?.[0];
    const type = question?.type?.toLowerCase() || '';
    
    // Get previously selected answers (where status === true)
    const savedAnswers: any[] = question?.answers?.filter((ans: any) => ans?.status === true) || [];
    
    let initialAnswer: any = {
      rating: firstAnswer?.rating ?? null,
      explanation: '',
      selectedAnswerIds: [],
      selectedAnswers: [],
      duration: 0,
      startTime: Date.now(),
    };
    
    // For satisfaction scale - load saved selection
    if (type.includes('satisfaction scale') && savedAnswers.length > 0) {
      initialAnswer.selectedAnswerIds = [savedAnswers[0].id];
      initialAnswer.explanation = savedAnswers[0]?.selected_answer_text || savedAnswers[0].text;
    }
    
    // For select type - load all saved selections
    if (type.includes('select') && savedAnswers.length > 0) {
      initialAnswer.selectedAnswers = savedAnswers.map((ans: any) => ({
        answerId: ans.id,
        text: ans?.selected_answer_text || ans?.text
      }));
    }
    
    // For free text - load saved text
    if (type.includes('free text') && firstAnswer?.selected_answer_text) {
      initialAnswer.explanation = firstAnswer?.selected_answer_text;
    }
    
    setAnswers((prev) => ({
      ...prev,
      [question.id]: initialAnswer,
    }));
  }
}, [question.id]);

  // Track time per question
  React.useEffect(() => {
    const existingAnswer = answers[question.id];
    const firstAnswer = question?.answers?.[0];
    
    // setAnswers((prev) => ({
    //   ...prev,
    //   [question.id]: {
    //     rating: existingAnswer?.rating ?? firstAnswer?.rating ?? null,
    //     explanation: existingAnswer?.explanation ?? firstAnswer?.text ?? '',
    //     selectedAnswerIds: existingAnswer?.selectedAnswerIds ?? [],
    //     duration: existingAnswer?.duration ?? 0,
    //     startTime: Date.now(),
    //   },
    // }));

    // console.log({
    //   [question.id]: {
    //     rating: existingAnswer?.rating ?? firstAnswer?.rating ?? null,
    //     explanation: existingAnswer?.explanation ?? firstAnswer?.text ?? '',
    //     selectedAnswerIds: existingAnswer?.selectedAnswerIds ?? [],
    //     duration: existingAnswer?.duration ?? 0,
    //     startTime: Date.now(),
    //   },
    // }, 'current question answers');
    
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setError('');
  }, [question.id, setAnswers]);

  const handleRating = (value: number | null) => {
    setAnswers((prev) => ({
      ...prev,
      [question.id]: {
        ...prev[question.id],
        rating: value,
      },
    }));
    setError('');
  };

  const handleExplanationChange = (value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [question.id]: {
        ...prev[question.id],
        explanation: value,
      },
    }));
    setError('');
  };

  // Handle satisfaction scale selection (radio buttons)
  const handleSatisfactionScaleSelect = (answerId: string, text: string) => {
    setAnswers((prev) => ({
      ...prev,
      [question.id]: {
        ...prev[question.id],
        selectedAnswerIds: [answerId],
        explanation: text,
      },
    }));
    setError('');
  };

  // Handle select type (checkboxes - multiple selection)
  const handleSelectToggle = (answerId: string, text: string) => {
    setAnswers((prev: any) => {
      const current = prev[question.id]?.selectedAnswerIds || [];
      const currentAnswers = prev[question.id]?.selectedAnswers || [];
      const isSelected = currentAnswers?.find((answer: any) => answer.answerId === answerId) !== undefined;
      //currentAnswers.some((answer: any) => answer.answerId === answerId);
      console.log({isSelected, currentAnswers});
      
      return {
        ...prev,
        [question.id]: {
          ...prev[question.id],
          selectedAnswers: isSelected
            ? currentAnswers.filter( (answer: any) => answer.answerId !== answerId)
            : [...currentAnswers, {answerId, text}],
          // selectedAnswerIds: isSelected
          //   ? current.filter(id => id !== answerId)
          //   : [...current, answerId],
        },
      };
    });
    setError('');
  };

  // const validateAnswer = () => {
  //   const current = answers[question.id];
  //   if (!current) return false;

  //   const type = question?.type?.toLowerCase() || '';
  //   const needsText = type.includes('free text');
  //   const needsRating = type.includes('rating') && !type.includes('satisfaction');
  //   const needsScoring = type.includes('scoring');
  //   const needsSatisfactionScale = type.includes('satisfaction scale');
  //   const needsSelect = type.includes('select');

  //   if (needsText && !current.explanation?.trim()) return false;
  //   if ((needsRating || needsScoring) && current.rating === null) return false;
  //   if ((needsSatisfactionScale || needsSelect) && (!current.selectedAnswerIds || current.selectedAnswerIds.length === 0)) return false;

  //   return true;
  // };

  const validateAnswer = () => {
  const current = answers[question.id];
  if (!current) return false;

  const type = question?.type?.toLowerCase() || '';
  const needsText = type.includes('free text');
  const needsRating = type.includes('rating');
  const needsScoring = type.includes('scoring');
  const needsSatisfactionScale = type.includes('satisfaction scale');
  const needsSelect = type.includes('select');

  // "free text + rating" or "free text + scoring" must have both
  if ((needsText && needsRating) || (needsText && needsScoring)) {
    return current.explanation?.trim() && current.rating !== null;
  }

  if (needsText && !current.explanation?.trim()) return false;
  if ((needsRating || needsScoring) && current.rating === null) return false;
  if ((needsSatisfactionScale ) && (!current.selectedAnswerIds?.length)) return false;
  if ((needsSelect ) && (!current.selectedAnswers?.length)) return false;

  return true;
};


  const saveDuration = () => {
    setAnswers((prev) => {
      const q = prev[question.id];
      const duration = q?.duration ?? 0;
      const startTime = q?.startTime ?? Date.now();
      return {
        ...prev,
        [question.id]: {
          ...q,
          duration: duration + Math.floor((Date.now() - startTime) / 1000),
        },
      };
    });
  };

  const handlePrev = () => {
    saveDuration();
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
    setError('');
  };

  const handleNext = () => {
    if (!validateAnswer()) {
      const type = question?.type?.toLowerCase() || '';
      if (type.includes('satisfaction scale') || type.includes('select')) {
        setError('Please select an option before continuing.');
      } else if (type.includes('free text') && !currentAnswer.explanation) {
        setError('Please provide an explanation before continuing.');
      } else {
        setError('Please provide a valid rating before continuing.');
      }
      return;
    }
    saveDuration();
    setCurrentIndex((prev) => Math.min(prev + 1, questions.length - 1));
  };

  const handleSubmit = () => {
    if (!validateAnswer()) {
      setError('Please complete the required fields before submitting.');
      return;
    }
    saveDuration();

    const payload = {
      time_left: 3560,
      attempt: 1,
      questions: questions.map((q) => {
        const a = answers[q.id] || { 
          rating: null, 
          explanation: '', 
          duration: 0,
          selectedAnswerIds: [],
          selectedAnswers: [],
        };
        
        // For satisfaction scale and select types, create answer objects for each selected ID
        const type = q?.type?.toLowerCase() || '';
        let answersArray = [];
        
        if (type.includes('satisfaction scale')) {
          answersArray = a.selectedAnswerIds.map(answerId => ({
            answer_id: answerId,
            answer_text: a.explanation,
            rating: null,
            status: true,
          }));
        }
        else if (type.includes('select')) {
          answersArray = a.selectedAnswers.map(answer => ({
            answer_id: answer.answerId,
            answer_text: answer.text,
            rating: null,
            status: true,
          }));
        }
         else {
          answersArray = [{
            answer_id: null,
            answer_text: a.explanation || null,
            rating: a.rating || null,
            status: true,
          }];
        }

        return {
          question_id: q.id,
          duration: a.duration || 0,
          answers: answersArray,
        };
      }),
    };
    //return console.log(payload, 'payload');
    
    updateAnswersMutation.mutate({
      reviewerId: assessmentType === 'review' ? userId : null,
      userId: assessmentType === 'review' ? activeAssessment?.user_id : userId,
      examId: activeAssessment?.exam_id,
      userExamId: window.sessionStorage.getItem('active-assessment-id'),
      data: payload,
    } as any);
  };

  const handleSaveDraft = () => {
    const q = questions.map((q) => {
      const a = answers[q.id] || { 
        rating: null, 
        explanation: '', 
        duration: 0,
        selectedAnswerIds: [],
        selectedAnswers: []
      };
      
      const type = q?.type?.toLowerCase() || '';
      let answersArray = [];
      
      if (type.includes('satisfaction scale')) {
        answersArray = a.selectedAnswerIds.map(answerId => ({
          answer_id: answerId,
          answer_text: a.explanation,
          rating: null,
          status: true,
        }));
      } 
      else if(type.includes('select')) {
        answersArray = a.selectedAnswers.map(answer => ({
          answer_id: answer.answerId,
          answer_text: answer.text,
          rating: null,
          status: true,
        }));
      }
      else {
        answersArray = [{
          answer_id: null,
          answer_text: a.explanation,
          rating: a.rating,
          status: true,
        }];
      }

      return {
        question_id: q.id,
        duration: a.duration || 0,
        answers: answersArray,
      };
    });

    const payload = {
      time_left: 3560,
      attempt: 1,
      questions: q?.length ? q?.filter(question => 
        question?.answers?.some((ans: any) => 
          (ans?.rating || ans?.answer_text || ans?.answer_id)
        )
      ) : [],
    };

    draftAnswersMutation.mutate({
      reviewerId: assessmentType === 'review' ? userId : null,
      userId: assessmentType === 'review' ? activeAssessment?.user_id : userId,
      examId: activeAssessment?.exam_id,
      userExamId: window.sessionStorage.getItem('active-assessment-id'),
      data: payload,
    } as any);
  };

  const config = useMemo(() => ({
    readonly: false,
    placeholder: '',
    style: {
      ul: { 'list-style-type': 'disc', 'padding-left': '20px' },
      ol: { 'list-style-type': 'decimal', 'padding-left': '20px' }
    }
  }), []);

  // Extract rating value from satisfaction scale text (e.g., "Very Satisfied=5" -> 5)
  const extractRatingFromText = (text: string): number | null => {
    const match = text.match(/=(\d+)$/);
    return match ? parseInt(match[1]) : null;
  };

  // Get clean label for satisfaction scale
  const getCleanLabel = (text: string): string => {
    return text.split('=')[0];
  };

  const questionType = question?.type?.toLowerCase() || '';

  console.log({answers});
  
  return (
    <div className='text-lg'>
      <header className='bg-white px-3 rounded-lg py-2 mt-2'>
        <div className='flex justify-between mb-2'>
          <h1 className='text-lg'>
            Question {currentIndex + 1} of {questions.length}
          </h1>
          <p className='text-lg'>
            <b className='text-lg'>Section</b>: {question?.section?.title}
          </p>
        </div>
        <ProgressBar current={currentIndex + 1} total={questions.length} />
      </header>

      <div className='bg-white mt-8 p-5 pt-2 rounded-lg mx-auto md:max-w-[700px]'>
        <p className='text-lg text-primary'>
          <b className='text-lg'>{question?.tag}</b>
        </p>

        <section className='grid grid-cols-1 gap-0'>
          <div className='md:max-w-[700px]'>
            <h2 className='text-lg mt-5 mb-3'>{question.text}</h2>

            {/* Free Text Editor */}
            {(questionType.includes('free text') || questionType.includes('free_text') ) && (
              <div className='py-3 editor-container'>
                <JoditEditor
                  ref={editor}
                  value={currentAnswer.explanation ?? ''}
                  config={config}
                  tabIndex={1}
                  onBlur={newContent => {
                    handleExplanationChange(newContent);
                  }}
                />
              </div>
            )}

            {/* Scoring Type - Number Input */}
            {questionType.includes('scoring') && (
              <section>
                <div className='flex flex-col mt-3'>
                  <p className='text-lg font-semibold mb-2'>
                    Score <span className='font-normal'>(Required)</span>
                  </p>
                  <div className='flex items-center'>
                    <input
                      type="number"
                      min={question.rating_range_from || 0}
                      max={question.rating_range_to || 10}
                      step="0.1"
                      value={currentAnswer.rating ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        const numValue = value === '' ? null : parseFloat(value);
                        
                        if (numValue !== null) {
                          if (numValue < (question.rating_range_from || 0) || 
                              numValue > (question.rating_range_to || 10)) {
                            setError(`Score must be between ${question.rating_range_from || 0} and ${question.rating_range_to || 10}`);
                          } else {
                            setError('');
                          }
                        }
                        
                        handleRating(numValue);
                      }}
                      className='w-24 h-10 border border-gray-300 rounded-md px-3 focus:outline-none focus:border-primary'
                      placeholder="Score"
                    />
                    {/* <span className='ml-2 text-gray-600'>
                      (Range: {question.rating_range_from || 0} - {question.rating_range_to || 10})
                    </span> */}
                  </div>
                  {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                </div>
              </section>
            )}

            {/* Regular Rating Type - Star Rating */}
            {questionType.includes('rating') && !questionType.includes('satisfaction') && (
              <section>
                <div className='flex items-center justify-between mt-3'>
                  <p className='text-lg font-semibold'>
                    Rating <span className='font-normal'>(Required)</span>
                  </p>
                  <div className='flex items-center'>
                    <Rating
                      name={`rating-${question.id}`}
                      value={currentAnswer.rating ?? 0}
                      precision={1}
                      sx={{ "& .MuiRating-icon": { fontSize: "40px" } }}
                      onChange={(event, newValue) => {
                        handleRating(newValue);
                      }}
                      max={question.rating_range_to || 5}
                      onChangeActive={(event, newHover) => setHover(newHover)}
                      emptyIcon={<StarIcon style={{ opacity: 0.3, fontSize: "40px" }} />}
                    />
                    {currentAnswer.rating !== null && (
                      <h2 className='text-xl pl-2'>{currentAnswer.rating}</h2>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Satisfaction Scale - Radio Buttons Grid */}
            {questionType.includes('satisfaction scale') && (
              <div className='mt-5'>
                <div className='overflow-x-auto'>
                  <table className='w-full border-collapse'>
                    <thead>
                      <tr className='border-b-2 border-gray-300'>
                        {/* <th className='text-left p-3 font-normal'></th> */}
                        {question.answers
                          .sort((a, b) => {
                            const ratingA = extractRatingFromText(a.text) || 0;
                            const ratingB = extractRatingFromText(b.text) || 0;
                            return ratingA - ratingB;
                          })
                          .map((answer) => (
                            <th key={answer.id} className='text-center p-3 font-semibold min-w-[100px]'>
                              {getCleanLabel(answer.text)}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className='border-b border-gray-200'>
                        {/* <td className='p-3 pr-8'>{question.text}</td> */}
                        {question.answers
                          .sort((a, b) => {
                            const ratingA = extractRatingFromText(a.text) || 0;
                            const ratingB = extractRatingFromText(b.text) || 0;
                            return ratingA - ratingB;
                          })
                          .map((answer) => (
                            <td key={answer.id} className='text-center p-3'>
                              <input
                                type='radio'
                                name={`satisfaction-${question.id}`}
                                checked={currentAnswer?.selectedAnswerIds?.includes(answer?.id)}
                                onChange={() => handleSatisfactionScaleSelect(answer.id, getCleanLabel(answer.text))}
                                className='w-6 h-6 cursor-pointer accent-primary'
                              />
                            </td>
                          ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Select Type - Checkboxes */}
            {questionType.includes('select') && (
              <div className='mt-5 space-y-3'>
                {question.answers.map((answer) => (
                  <label
                    key={answer.id}
                    className='flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition'
                  >
                    <input
                      type='checkbox'
                      checked={currentAnswer?.selectedAnswers?.find((a: any) => a?.answerId === answer?.id)
                        === undefined ? false : true
                      }
                      onChange={() => handleSelectToggle(answer.id, answer.text)}
                      className='w-5 h-5 cursor-pointer accent-primary mr-3'
                    />
                    <span className='text-base'>{answer.text}</span>
                  </label>
                ))}
              </div>
            )}

            {error && <p className='text-red-500 mt-3 text-sm'>{error}</p>}
          </div>
        </section>

        <section className='flex flex-col sm:flex-row justify-between bg-white mx-auto md:max-w-[700px] gap-3'>
          <button
            disabled={currentIndex === 0}
            className={`border border-primary text-primary font-semibold rounded-lg py-3 px-6 sm:px-12 md:px-16 mt-5 ${
              currentIndex === 0
                ? 'text-gray-400 border-gray-300 cursor-not-allowed'
                : 'text-primary'
            }`}
            onClick={handlePrev}
          >
            Previous
          </button>

          <div className='flex flex-col sm:flex-row items-center gap-3 sm:gap-5'>
            <button
              className='bg-orange-500 rounded-lg text-white py-3 px-6 sm:px-12 md:px-16 mt-5 w-full sm:w-auto'
              onClick={() => handleSaveDraft()}
            >
              Save Draft
            </button>
            {currentIndex === questions.length - 1 ? (
              <button
                className='bg-primary rounded-lg text-white py-3 px-6 sm:px-12 md:px-16 mt-5 w-full sm:w-auto'
                onClick={handleSubmit}
                disabled={updateAnswersMutation.isLoading || finishSurveyMutation.isLoading}
              >
                {
                  (updateAnswersMutation.isLoading || finishSurveyMutation.isLoading) ? <LoaderIcon /> : 'Submit'
                }
              </button>
            ) : (
              <button
                className='bg-primary rounded-lg text-white py-3 px-6 sm:px-12 md:px-16 mt-5 w-full sm:w-auto'
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
           
        </section>
      </div>
    </div>
  );
};

export default Employee